// src/components/walkthrough/WalkthroughMount.jsx
//
// The first-sign-in check (docs/changes/onboarding-walkthrough/SPEC.md): the
// one place that decides whether the walkthrough plays on its own, and what
// finishing or skipping it records. Mounted once in App.jsx, inside
// RequireOrganization so it waits for the chooser, and outside the route table
// so it lives across the walkthrough's own navigation.
//
// - It asks once per page load, the first time Home is open, and plays only on
//   Home (SPEC decision 4).
// - It plays the steps of the person's version they have not seen
//   (criterion 6), and Skip or Done records every step of the version
//   (decision 3). A tab closed mid-way records nothing.
// - A failed GET plays nothing and writes nothing (PLAN decision 1): the
//   walkthrough must never stand between someone and their work.
// - Help's replay arrives here as navigation state (help-module SPEC
//   decisions 4-7): every step of the version from step 1, whatever has been
//   seen, recording nothing and counted apart from first runs. It takes the
//   place of the first-sign-in check for that visit.

import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { apiClient } from "../../utils/apiClient";
import { track } from "../../utils/analytics";
import { useRole } from "../../hooks/useRole.ts";
import { useOrganization } from "../../hooks/useOrganization";
import { isWalkthroughReplay } from "../../utils/walkthroughReplay";
import { stepsFor } from "./steps";

// Loaded only when someone is about to see it.
const Walkthrough = lazy(() => import("./Walkthrough"));

/** The signed-in person's Cognito sub, so no step rings their own row. */
function useCallerSub() {
  const [sub, setSub] = useState(null);
  useEffect(() => {
    let live = true;
    Auth.currentAuthenticatedUser()
      .then((user) => {
        if (live)
          setSub(user?.signInUserSession?.idToken?.payload?.sub ?? null);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  return sub;
}

export default function WalkthroughMount() {
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const { loading, isAdmin, canEditJob } = useRole();
  const { available } = useOrganization();
  const sub = useCallerSub();

  const asked = useRef(false);
  const [pending, setPending] = useState(null); // { steps, version }
  const [run, setRun] = useState(null); // { steps, version, replay? }

  const versionNow = () =>
    stepsFor({ isAdmin, canEdit: canEditJob, organizations: available });

  // A replay from Help. Declared before the check so it runs first.
  useEffect(() => {
    if (loading || pathname !== "/" || !isWalkthroughReplay(state)) return;
    // History state survives a reload, so the request goes before anything
    // plays; a reload of Home must not replay it.
    navigate("/", { replace: true, state: null });
    asked.current = true;
    setPending(null);
    const version = versionNow();
    setRun({ steps: version, version, replay: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pathname, state]);

  useEffect(() => {
    if (loading || asked.current || pathname !== "/") return;
    if (isWalkthroughReplay(state)) return;
    asked.current = true;
    const version = versionNow();
    apiClient
      .get("/my-walkthrough")
      .then(({ data }) => {
        const seen = Array.isArray(data?.seen) ? data.seen : null;
        if (!seen) return; // not the route's answer: play nothing
        const steps = version.filter((step) => !seen.includes(step.id));
        if (steps.length > 0) setPending({ steps, version });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pathname, state]);

  // Starts on Home only: someone who left Home while the answer was on its
  // way sees it when they come back.
  useEffect(() => {
    if (pending && !run && pathname === "/") {
      setRun(pending);
      setPending(null);
    }
  }, [pending, run, pathname]);

  if (!run) return null;

  const onEnd = (reason, at) => {
    setRun(null);
    if (run.replay) return; // seen already: nothing to record or count
    apiClient
      .put("/my-walkthrough", { ids: run.version.map((step) => step.id) })
      .catch(() => {});
    track(
      reason === "skipped" ? "walkthrough_skipped" : "walkthrough_finished",
      { metadata: { step: at + 1, of: run.steps.length } },
    );
  };

  return (
    <Suspense fallback={null}>
      <Walkthrough steps={run.steps} onEnd={onEnd} sub={sub} />
    </Suspense>
  );
}
